import { RowConfig } from '@/shared/types/table';

export const toggleRowSelection = (
  selectedIds: (string | number)[],
  rowId: string | number
): (string | number)[] => {
  const isSelected = selectedIds.includes(rowId);
  return isSelected ? selectedIds.filter((id) => id !== rowId) : [...selectedIds, rowId];
};

/**
 * Aplica "seleccionar todo" sobre un conjunto de filas (p. ej. página visible).
 * Con `accumulative`, al desmarcar solo quita ids de ese conjunto; al marcar, une con la selección previa.
 */
export const applyPageSelectAll = <T>(
  checked: boolean,
  selectedIds: (string | number)[],
  pageRows: RowConfig<T>[],
  accumulative: boolean
): (string | number)[] => {
  const pageIds = pageRows.map((row) => row.id);
  if (pageIds.length === 0) {
    return selectedIds;
  }

  if (!accumulative) {
    return checked ? pageIds : [];
  }

  const pageIdSet = new Set(pageIds);
  if (checked) {
    return Array.from(new Set([...selectedIds, ...pageIds]));
  }
  return selectedIds.filter((id) => !pageIdSet.has(id));
};

/** Toggle de selección de página (modo acumulativo). */
export const selectAllRows = <T>(
  rows: RowConfig<T>[],
  currentSelection: (string | number)[]
): (string | number)[] => {
  const allOnPageSelected = isAllSelected(rows, currentSelection);
  return applyPageSelectAll(!allOnPageSelected, currentSelection, rows, true);
};

export const selectRange = (
  selectedIds: (string | number)[],
  allIds: (string | number)[],
  fromId: string | number,
  toId: string | number
): (string | number)[] => {
  const fromIndex = allIds.indexOf(fromId);
  const toIndex = allIds.indexOf(toId);

  if (fromIndex === -1 || toIndex === -1) return selectedIds;

  const start = Math.min(fromIndex, toIndex);
  const end = Math.max(fromIndex, toIndex);

  const rangeIds = allIds.slice(start, end + 1);
  const newSelection = new Set([...selectedIds, ...rangeIds]);

  return Array.from(newSelection);
};

export const isAllSelected = <T>(
  rows: RowConfig<T>[],
  selectedIds: (string | number)[]
): boolean => {
  if (rows.length === 0) return false;
  return rows.every((row) => selectedIds.includes(row.id));
};

export const isSomeSelected = <T>(
  rows: RowConfig<T>[],
  selectedIds: (string | number)[]
): boolean => {
  if (selectedIds.length === 0) return false;
  return rows.some((row) => selectedIds.includes(row.id)) && !isAllSelected(rows, selectedIds);
};
